import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { LoanAmountFields } from "./LoanAmountFields";
import { useLoanForm } from "../hooks/useLoanForm";
import { loanPayload } from "../lib/loanForm";
import api, { formatApiError } from "../lib/api";

const NEW_LOAN_FIELD_IDS = {
  amount: "new-loan-amount",
  amountTestId: "new-loan-amount-input",
  interest: "new-interest-amount",
  interestTestId: "new-interest-amount-input",
  deposit: "new-deposit-amount",
  depositTestId: "new-deposit-amount-input",
  stampDuty: "new-stamp-duty",
  stampDutyTestId: "new-stamp-duty-input",
  cashOnHandTestId: "new-cash-on-hand-value",
  outstandingTestId: "new-outstanding-value",
  loanDate: "new-loan-date",
  loanDateTestId: "new-loan-date-input",
  termDays: "new-term-days",
  termDaysTestId: "new-term-days-input",
  dueDate: "new-due-date",
  dueDateTestId: "new-due-date-input",
  dueDateLabel: "Due Date",
  remarks: "new-loan-remarks",
  remarksTestId: "new-loan-remarks-input",
};

export default function NewLoanDialog({ customers, onClose, onDone, open }) {
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerId, setCustomerId] = useState("");
  const loanForm = useLoanForm();
  const matches = useMemo(() => {
    const query = customerQuery.toLowerCase();
    return customers.filter((customer) => {
      const searchValue = `${customer.full_name} ${customer.ic_passport} ${customer.nickname || ""}`;
      return searchValue.toLowerCase().includes(query);
    });
  }, [customers, customerQuery]);

  const submit = async (event) => {
    event.preventDefault();
    loanForm.setSubmitted(true);
    if (!customerId || !loanForm.isComplete()) {
      toast.error("Please complete every loan field before creating the loan.");
      return;
    }
    loanForm.setSaving(true);
    try {
      await api.post(`/customers/${customerId}/loans`, loanPayload(loanForm.form));
      toast.success("New loan created");
      onDone();
      onClose();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      loanForm.setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="borrow-loan-dialog new-loan-dialog" data-testid="new-loan-dialog">
        <DialogHeader>
          <DialogTitle>New Loan</DialogTitle>
          <DialogDescription>
            Select a customer and enter the loan details to create a new record.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="borrow-loan-form" noValidate>
          <div className="new-loan-customer">
            <Label htmlFor="customer-loan-search">Customer *</Label>
            <Input
              id="customer-loan-search"
              data-testid="customer-loan-search-input"
              className={loanForm.submitted && !customerId ? "borrow-loan-field-error" : ""}
              value={customerQuery}
              onChange={(event) => setCustomerQuery(event.target.value)}
              placeholder="Search by name, IC or customer nickname..."
            />
            <select
              value={customerId}
              onChange={(event) => setCustomerId(event.target.value)}
              data-testid="customer-loan-select"
              className={loanForm.submitted && !customerId ? "borrow-loan-field-error" : ""}
            >
              <option value="">Select customer</option>
              {matches.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.full_name}
                  {customer.nickname ? ` · ${customer.nickname}` : ""} · {customer.ic_passport}
                </option>
              ))}
            </select>
          </div>
          <LoanAmountFields
            calculations={loanForm.calculations}
            form={loanForm.form}
            ids={NEW_LOAN_FIELD_IDS}
            isInvalid={loanForm.isInvalid}
            onChange={loanForm.update}
          />
          <div className="borrow-loan-actions">
            <Button
              type="submit"
              disabled={loanForm.saving}
              data-testid="create-new-loan-button"
            >
              {loanForm.saving ? "Creating…" : "Create Loan"}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              data-testid="cancel-new-loan-button"
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}